import React from "react";
import { useBranchFilter } from "../../features/branchFilter/useBranchFilter";
import { useDashboardPeriod } from "../../features/dashboard/useDashboardPeriod";
import DateRangeFilter from "../../features/stock/components/list/DateRangeFilter";

// Compact "Branch: X | Period: ▾ | Compare: ▾" control bar (section 3/17) -
// Branch is read-only display here, sourced from the *existing* global
// branch picker (useBranchFilter/BranchPicker already in the app Header) -
// deliberately not a second branch selector, per "reuse the existing
// branch filtering mechanism, don't duplicate state management."
// Period/Compare are the two genuinely new pieces of global state, both
// resolved through the one shared periodUtils.js engine so no widget ever
// computes its own date math. The two "Custom..." date-range pickers reuse
// the same DateRangeFilter component the Stock module's own list pages
// already use, rather than a new date-input pair.
const DashboardPeriodControl = () => {
  const { selectedBranchId, availableBranches, showAllOption, allOptionLabel } = useBranchFilter();
  const {
    periodKey, setPeriod, periodOptions, customPeriodFrom, customPeriodTo, setCustomPeriod,
    compareKey, setCompare, compareOptions, customCompareFrom, customCompareTo, setCustomCompare,
    period, comparison,
  } = useDashboardPeriod();

  const branchLabel = selectedBranchId
    ? availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode || "Branch"
    : showAllOption
    ? allOptionLabel
    : availableBranches.map((b) => b.branchCode).join(", ") || "No Branch Access";

  return (
    <div className="card mb-3 dashboard-period-control">
      <div className="card-body py-2">
        {/* Row 1: the three selectors, always inline - fixed as its own
            flex row (not relying on flex-wrap against the date-range text
            below) so it can never wrap into an unlabeled-looking stack. */}
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small text-uppercase fw-semibold">Branch</span>
            <span className="fw-semibold small">{branchLabel}</span>
          </div>
          <div className="vr d-none d-md-block" />
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small text-uppercase fw-semibold">Period</span>
            <select
              className="form-control form-control-sm"
              style={{ width: 150 }}
              value={periodKey}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {periodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {periodKey === "CUSTOM" && (
              <DateRangeFilter
                dateFrom={customPeriodFrom || ""}
                setDateFrom={(v) => setCustomPeriod(v, customPeriodTo || v)}
                dateTo={customPeriodTo || ""}
                setDateTo={(v) => setCustomPeriod(customPeriodFrom || v, v)}
              />
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small text-uppercase fw-semibold">Compare</span>
            <select
              className="form-control form-control-sm"
              style={{ width: 170 }}
              value={compareKey || ""}
              onChange={(e) => setCompare(e.target.value)}
            >
              {compareOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {compareKey === "CUSTOM" && (
              <DateRangeFilter
                dateFrom={customCompareFrom || ""}
                setDateFrom={(v) => setCustomCompare(v, customCompareTo || v)}
                dateTo={customCompareTo || ""}
                setDateTo={(v) => setCustomCompare(customCompareFrom || v, v)}
              />
            )}
          </div>
        </div>
        {/* Row 2: the resolved dates - always its own line, so it never
            competes with the selectors for horizontal space. */}
        <div className="text-muted small mt-1">
          {period.dateFrom} to {period.dateTo}
          {comparison?.type === "single" && ` · vs ${comparison.dateFrom} to ${comparison.dateTo}`}
          {comparison?.type === "multi" && ` · vs ${comparison.windows.map((w) => w.label).join(", ")}`}
        </div>
        {/* Said out loud, same wording as the phone app, so a Monday "Yesterday"
            showing Saturday reads as intended rather than as a wrong date. */}
        {period.note && <div className="small mt-1" style={{ color: "#8a6d1f" }}>{period.note}</div>}
      </div>
    </div>
  );
};

export default DashboardPeriodControl;
